import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import { DIALOG_TYPE } from '../../../../../constants/DialogTypes';
import { Button, DataTable, Dialog, NameEmailAvatar } from '../../../../../ui';
import { Tabs } from '../../../../../ui/components/Common/Tabs/Tabs';
import { Refresh } from '../../../../../ui/icons/icons';
import { UploadFileComponent } from '../../../../../ui/input/FileInput';
import TopBanner from '../../../../../ui/TopBanner';
import { apiUrl } from '../../../../../utils/api_service/api_url';
import { getJobApplicants, sendInviteApplicants } from '../../../../../utils/api_service/JobOpenings';
import { JobOpeningsContext } from '../../../../../utils/contexts/JobOpening';
import { useUploadForm } from '../../../../../utils/helpers/api_utils';
import { formatUnixPretty } from '../../../../../utils/helpers/time';
import {
  JobApplicant,
  JobDescription,
  JobRequisition,
} from '../../../../../utils/types/JobOpenings';
import { TooltipWrapper } from '../../../view/Functions';
import { DocumentChecklist } from '../../../viewJobPosting/JobPostDetails/components/DocumentChecklist';
import InviteListModal from './InviteList/InviteListModal';

type ApplicantStatus = {
  Completed: number;
  InProgress: number;
  Error: number;
};

type CVIngestionProps = {
  completedElement: () => void;
  setStages: (value: any) => void;
  jobRequisition: JobRequisition;
  jobDescription: JobDescription;
};

const CVIngestionTab = ({
  completedElement,
  setStages,
  jobDescription,
}: CVIngestionProps) => {
  const { id } = useParams<{ id: string }>();
  const { completedJobOpeningDetails } = useContext(JobOpeningsContext);
  const uploadURL = apiUrl + `/job-openings/${id}/cv-ingestion/file`;
  const { uploadStatus, uploadForm, progress } = useUploadForm(uploadURL);
  const [uploadedFile, setUploadedFile] = useState<File[] | null>(null);
  const [modalState, setModalState] = useState<boolean>(false);
  const [jobApplicants, setJobApplicants] = useState<JobApplicant[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [ApplicantStatus, setApplicantStatus] = useState<ApplicantStatus>({
    Completed: 0,
    InProgress: 0,
    Error: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [inviteLoading, setInviteLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] =
    useState<'Bulk Upload' | 'Single Candidate Upload'>('Bulk Upload');
  const [selectedApplicants, setSelectedApplicants] = useState<string[]>([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);

  // A document is considered "extra" if it's not the Resume
  const isResume = (key: string) => key === 'resume';

  const mandatoryDocs =
    jobDescription?.documentRequirements?.filter(
      (req) => req.isMandatory && !isResume(req.key),
    ) ?? [];
  const hasExtraDocs = (jobDescription?.documentRequirements?.length ?? 0) > 1;

  const showTabs = false;
  const forceSingleUpload = hasExtraDocs;

  useEffect(() => {
    if (forceSingleUpload) {
      setActiveTab('Single Candidate Upload');
    } else {
      setActiveTab('Bulk Upload');
    }
  }, [forceSingleUpload]);

  const [singleCandidateFiles, setSingleCandidateFiles] = useState<
    Record<string, File[]>
  >({});

  const canSubmit = useMemo(() => {
    if (uploadStatus === 'loading') return false;
    if (activeTab === 'Bulk Upload') {
      return uploadedFile && uploadedFile.length > 0;
    } else {
      // Single Candidate Upload
      const hasResume =
        jobDescription?.documentRequirements?.some(
          (req) =>
            isResume(req.key) && singleCandidateFiles[req.key]?.length > 0,
        ) ?? false;
      const hasAllMandatory =
        mandatoryDocs?.every(
          (doc: any) => singleCandidateFiles[doc.key]?.length > 0,
        ) ?? false;
      return hasResume && hasAllMandatory;
    }
  }, [
    activeTab,
    uploadedFile,
    singleCandidateFiles,
    mandatoryDocs,
    uploadStatus,
  ]);

  const getAllowMultiple = (type: string): boolean => {
    const req = jobDescription?.documentRequirements?.find(
      (r) => r.key === type,
    );
    return req?.allowMultiple ?? false;
  };

  const handleFileSelect = (typeOrKey: string, file: File | null) => {
    setSingleCandidateFiles((prev) => {
      const newFiles = { ...prev };
      if (file) {
        const canMultiple = getAllowMultiple(typeOrKey);
        if (canMultiple) {
          newFiles[typeOrKey] = [...(prev[typeOrKey] || []), file];
        } else {
          newFiles[typeOrKey] = [file]; // replace
        }
      } else {
        delete newFiles[typeOrKey];
      }
      return newFiles;
    });
  };

  const handleFileRemoveAtIndex = (type: string, index: number) => {
    setSingleCandidateFiles((prev) => {
      const files = prev[type] || [];
      const updatedFiles = [...files];
      updatedFiles.splice(index, 1);
      if (updatedFiles.length === 0) {
        const newState = { ...prev };
        delete newState[type];
        return newState;
      }
      return { ...prev, [type]: updatedFiles };
    });
  };

  const handleBatchFileSelect = (updates: { [type: string]: File | null }) => {
    setSingleCandidateFiles((prev) => {
      const newFiles = { ...prev };
      Object.entries(updates).forEach(([type, file]) => {
        if (file) {
          const canMultiple = getAllowMultiple(type);
          if (canMultiple) {
            newFiles[type] = [...(prev[type] || []), file];
          } else {
            newFiles[type] = [file];
          }
        } else {
          delete newFiles[type];
        }
      });
      return newFiles;
    });
  };

  useEffect(() => {
    fetchJobApplicants();
  }, [id, pageNumber]);

  const fetchJobApplicants = () => {
    setLoading(true);
    getJobApplicants(id, '', pageNumber, 10, '')
      .then((response) => {
        if (!response.ok) {
          throw new Error();
        }
        return response.json();
      })
      .then((data) => {
        setJobApplicants(data.jobApplicants);
        setTotalPages(Math.ceil(data.totalApplicants / 10));
        setApplicantStatus({
          Completed: data.completedApplicants,
          InProgress: data.inProgressApplicants,
          Error: data.errorApplicants,
        });
      })
      .catch(() => {
        toast.error('Error Getting Job Applicants', {
          position: 'bottom-center',
        });
        setTotalPages(1);
        setJobApplicants([]);
      })
      .finally(() => setLoading(false));
  };

  const uploadFiles = async () => {
    try {
      const formData = new FormData();
      let hasFiles = false;

      if (activeTab === 'Bulk Upload') {
        if (uploadedFile && uploadedFile.length > 0) {
          uploadedFile.forEach((file) => {
            formData.append('files', file);
          });
          hasFiles = true;
        }
      } else {
        // Single Candidate Upload
        Object.entries(singleCandidateFiles).forEach(([key, files]) => {
          if (files && files.length > 0) {
            files.forEach((file) => {
              formData.append(key, file);
            });
            hasFiles = true;
          }
        });
      }

      if (hasFiles) {
        const targetUrl =
          activeTab === 'Bulk Upload'
            ? uploadURL
            : apiUrl + `/job-openings/${id}/document/upload`;

        await uploadForm(formData, targetUrl);
        toast.success('Successfully Uploaded Files', {
          position: 'bottom-center',
        });
        completedElement();
        setStages((prevStages: any[]) =>
          prevStages.map((stage) =>
            stage.label === 'Shortlisting'
              ? { ...stage, status: 'In Progress' }
              : stage,
          ),
        );
        setUploadedFile(null);
        setSingleCandidateFiles({});
        setModalState(false);
        fetchJobApplicants();
      }
    } catch {
      toast.error('Failed Uploading Files', { position: 'bottom-center' });
    }
  };

  const handleCloseModal = () => {
    setModalState(false);
    setUploadedFile(null);
    setSingleCandidateFiles({});
  };

  const refreshApplicants = () => {
    setPageNumber(1);
    fetchJobApplicants();
    setSelectedApplicants([]);
  };

  const handleSelectRow = (row: JobApplicant) => {
    const applicantId = row.id;
    if (!applicantId) return;
    setSelectedApplicants((prev) =>
      prev.includes(applicantId)
        ? prev.filter((id) => id !== applicantId)
        : [...prev, applicantId],
    );
  };

  const handleSelectAll = () => {
    if (selectedApplicants.length === jobApplicants.length) {
      setSelectedApplicants([]);
    } else {
      const allIds = jobApplicants
        .map((a) => a.id)
        .filter((id): id is string => !!id);
      setSelectedApplicants(allIds);
    }
  };

  const handleSendInvite = async () => {
    if (selectedApplicants.length === 0) return;
    setInviteLoading(true);
    try {
      const response = await sendInviteApplicants(id, selectedApplicants);
      if (response.ok) {
        toast.success(
          `Successfully sent invites to ${selectedApplicants.length} applicants`,
          {
            position: 'bottom-center',
          },
        );
        setSelectedApplicants([]);
        fetchJobApplicants();
      } else {
        throw new Error();
      }
    } catch {
      toast.error('Failed to send invites', { position: 'bottom-center' });
    } finally {
      setInviteLoading(false);
    }
  };

  const columns = [
    {
      key: 'baseFilename',
      label: 'Filename',
      align: 'left' as const,
      render: (row: any) => <TooltipWrapper text={row.baseFilename} />,
    },
    {
      key: 'documentType',
      label: 'Document Type',
      align: 'left' as const,
      render: (row: any) => <TooltipWrapper text={row.documentType} />,
    },
    {
      key: 'details',
      label: 'Applicant Details',
      align: 'left' as const,
      render: (row: JobApplicant) => (
        <NameEmailAvatar
            name={(row.firstName ?? '') + ' ' + (row.lastName ?? '')}
            email={row.email ?? ''}
            avatar={row.base64Encoded}
        />
      ),
    },
    {
      key: 'createdAt',
      label: 'Date Uploaded',
      align: 'left' as const,
      render: (row: any) => (
        <TooltipWrapper text={formatUnixPretty(row.createdAt as number)} />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'left' as const,
      render: (row: JobApplicant) => (
        <>
          { row.status === 'Error' ? (
            <div className='inline-flex flex-col text-left'>
              <span className='px-2 py-0.5 bg-red-50 text-red-600 border border-red-100 text-[10px] font-bold uppercase rounded-md w-fit'>
                Error
              </span>
              <div
                  className='text-[10px] text-red-400 mt-1 max-w-[120px] truncate'
                  title={row.errorMessage}
              >
                { row.errorMessage }
              </div>
            </div>
          ) : (
            <span
                className={`relative px-2 py-0.5 text-[10px] font-bold uppercase rounded-md border w-fit
              ${
                row.status === 'Completed' || row.status === 'Success'
                  ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                  : 'bg-amber-50 text-amber-600 border-amber-100'
              }`}
            >
              { row.status }
            </span>
          ) }
        </>
      ),
    },
  ];

  return (
    <div className='flex min-w-0 flex-col gap-4 h-full'>
      { !completedJobOpeningDetails.includes('CV Ingestion') && (
        <TopBanner
            title='Final Check: Confirm Before Uploading'
            message='Once you upload the first applicant, all configurations tabs will be disabled, and their values will be locked. Please ensure all information is accurate before proceeding.'
            type='warning'
        />
      ) }
      <div className='flex flex-col gap-3 text-sm lg:flex-row lg:items-center lg:justify-between'>
        <div className='grid grid-cols-3 gap-2'>
          <div className='rounded-lg border border-emerald-100 bg-emerald-50 px-2.5 py-2 text-center'>
            <span className='block text-[10px] font-bold uppercase text-emerald-700'>
              Completed
            </span>
            <span className='text-base font-bold text-emerald-800'>
              { ApplicantStatus.Completed }
            </span>
          </div>
          <div className='rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2 text-center'>
            <span className='block text-[10px] font-bold uppercase text-blue-700'>
              Progress
            </span>
            <span className='text-base font-bold text-blue-800'>
              { ApplicantStatus.InProgress }
            </span>
          </div>
          <div className='rounded-lg border border-red-100 bg-red-50 px-2.5 py-2 text-center'>
            <span className='block text-[10px] font-bold uppercase text-red-700'>
              Error
            </span>
            <span className='text-base font-bold text-red-800'>
              { ApplicantStatus.Error }
            </span>
          </div>
        </div>
        <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:justify-end'>
          { selectedApplicants.length > 0 && (
            <Button
                onClick={handleSendInvite}
                label={
                  inviteLoading
                    ? 'Sending...'
                    : `Send Invite (${selectedApplicants.length})`
                }
                variant='primary'
                className='w-full !bg-emerald-600 hover:!bg-emerald-700 !border-none shadow-lg shadow-emerald-200/50 sm:w-auto'
                disabled={inviteLoading}
            />
          ) }
          <Button
              onClick={() => setIsInviteModalOpen(true)}
              label='Candibank Invites'
              variant='secondary'
              className='w-full !text-emerald-700 !border-emerald-200 hover:!bg-emerald-50 shadow-sm transition-all sm:w-auto'
          />
          <Button
              onClick={() => refreshApplicants()}
              label=''
              hasIcon
              icon={
                <Refresh className='h-6 w-6 stroke-primary-main fill-none group-hover:stroke-white hover:fill-none' />
              }
              variant='secondary'
              className='w-full sm:w-auto'
          />

          { showTabs ? (
            <Tabs
                tabs={[
                  { label: 'Bulk Upload (Resume Only)', value: 'Bulk Upload' },
                  {
                    label: 'Single Candidate Upload',
                    value: 'Single Candidate Upload',
                  },
                ]}
                activeTab={activeTab}
                onTabChange={(tab) => setActiveTab(tab as any)}
            />
          ) : (
            <Button
                onClick={() => setModalState(true)}
                label={forceSingleUpload ? 'Upload Application' : 'Upload Resume'}
                variant='primary'
                className='w-full sm:w-auto'
            />
          ) }

          { showTabs && (
            <Button
                onClick={() => setModalState(true)}
                label={
                  activeTab === 'Bulk Upload'
                    ? 'Upload Resume'
                    : 'Upload Application'
                }
                variant='primary'
                className='w-full sm:w-auto'
            />
          ) }
        </div>
      </div>
      <div className='flex-1 min-w-0 overflow-hidden min-h-[400px]'>
        <DataTable
            data={jobApplicants}
            columns={columns}
            isLoading={loading}
            currentPage={pageNumber}
            totalPages={totalPages}
            totalRecords={jobApplicants.length * totalPages}
            onPageChange={(page) => setPageNumber(page)}
            rowsPerPage={10}
            hasCheckBox
            onSelectAll={handleSelectAll}
            onSelectRow={(index, row: any) => handleSelectRow(row)}
            isRowSelected={(index, row: any) =>
              selectedApplicants.includes(row.id)
            }
            isAllSelected={
              selectedApplicants.length > 0 &&
            selectedApplicants.length === jobApplicants.length
            }
            isIndeterminate={
              selectedApplicants.length > 0 &&
            selectedApplicants.length < jobApplicants.length
            }
        />
      </div>
      <Dialog
          isOpen={modalState}
          onClose={handleCloseModal}
          title={
            activeTab === 'Bulk Upload' && !forceSingleUpload
              ? 'Bulk Upload Resumes'
              : 'Upload Application'
          }
          dialogType={DIALOG_TYPE.SUCCESS}
          justify='center'
          confirmButtonLable={
            uploadStatus === 'loading' ? 'Uploading...' : 'Upload'
          }
          cancelButtonLable='Cancel'
          handleApply={uploadFiles}
          disabled={!canSubmit}
          maxWidth='800px'
      >
        <div className=''>
          <p className='text-sm text-gray-600 mb-4'>
            { activeTab === 'Bulk Upload' && !forceSingleUpload
              ? 'Please select one or more resume files to upload in bulk.'
              : 'Please upload the following documents requested for this position.' }
          </p>
          { activeTab === 'Bulk Upload' && !forceSingleUpload ? (
            <UploadFileComponent
                uploadedFile={uploadedFile}
                setUploadedFile={setUploadedFile}
                validTypes={[
                  'application/pdf',
                  'application/msword',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                ]}
                isMultiple={true}
            />
          ) : (
            <DocumentChecklist
                documentRequirements={jobDescription?.documentRequirements ?? []}
                selectedFiles={singleCandidateFiles}
                onFileSelect={handleFileSelect}
                onBatchFileSelect={handleBatchFileSelect}
                onFileRemoveAtIndex={handleFileRemoveAtIndex}
                disabled={uploadStatus === 'loading'}
            />
          ) }

          { uploadStatus === 'loading' && (
            <div className='my-5'>
              <div className='h-4 rounded-md bg-gray-200 overflow-hidden'>
                <div
                    className='h-4 rounded-md bg-primary-gradient transition-all duration-300'
                    style={{ width: `${progress}%` }}
                ></div>
              </div>
              <p className='text-center text-xs text-gray-500 mt-2'>
                { Math.round(progress) }% Complete
              </p>
            </div>
          ) }
        </div>
      </Dialog>
      <InviteListModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          jobOpeningId={id || ''}
      />
    </div>
  );
};

export default CVIngestionTab;
